import React, { useState } from "react";
import { 
  Package, Layers, Ship, Users, Scale, Download, Calendar, Clock, 
  Mail, Plus, X, Trash2, FileText, Sparkles, AlertCircle, CheckCircle,
  TrendingUp, BarChart3, PieChart, Activity, Truck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

// Report type configurations
const REPORT_TYPES = [
  { 
    id: 'stock', 
    name: 'Stock Report', 
    description: 'Current inventory levels, material breakdown, capacity utilization, and 7-day movement trends',
    icon: Package,
    color: 'hsl(187,100%,42%)',
    frequency: 'Daily',
    features: ['Material breakdown by type', 'Capacity utilization charts', '7-day trend analysis', 'Site-by-site comparison']
  },
  { 
    id: 'stack', 
    name: 'Stack Report', 
    description: 'Storage bay analysis, material placement, space optimization metrics',
    icon: Layers,
    color: 'hsl(24.6,95%,53%)',
    frequency: 'Daily',
    features: ['Bay utilization overview', 'Material allocation map', 'Space efficiency metrics', 'Optimization recommendations']
  },
  { 
    id: 'vessel', 
    name: 'Vessel Report', 
    description: 'Vessel schedules, loading progress, ETA/ETD timelines, berth status',
    icon: Ship,
    color: '#22C55E',
    frequency: 'Daily',
    features: ['Active vessel tracking', 'Loading progress bars', 'Schedule adherence', 'Berth occupancy']
  },
  { 
    id: 'client', 
    name: 'Client Report', 
    description: 'Client activity summary, tonnage moved, performance comparisons',
    icon: Users,
    color: '#A855F7',
    frequency: 'Weekly',
    features: ['Top performers ranking', 'Tonnage analysis', 'Order history', 'Cycle time metrics']
  },
  { 
    id: 'weighbridge', 
    name: 'Weighbridge Report', 
    description: 'Daily weighing sessions, truck movements, cycle times, tonnage breakdown',
    icon: Scale,
    color: '#EC4899',
    frequency: 'Daily',
    features: ['Session summaries', 'Hourly distribution', 'Product breakdown', 'Efficiency metrics']
  },
  { 
    id: 'clientAllowance', 
    name: 'Client Allowance Report', 
    description: 'Transporter truck deliveries, allowance tracking, trucks on site, and mine list compliance',
    icon: Truck,
    color: '#14B8A6',
    frequency: 'Daily',
    features: ['Forecast vs received', 'Trucks on site (Tippers/Tauts)', 'Avg per day tracking', 'Mine list compliance']
  },
];

export default function ReportsHub() {
  const { toast } = useToast();
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState<string | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleName, setScheduleName] = useState("");
  const [selectedReportType, setSelectedReportType] = useState("");
  const [cronSchedule, setCronSchedule] = useState("0 8 * * *");
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [includeAI, setIncludeAI] = useState(true);
  const [schedules, setSchedules] = useState<any[]>([
    { id: 1, name: "Daily Stock Report", reportType: "stock", cron: "0 8 * * *", emails: ["admin@navig8.com"], active: true, lastSent: "2025-11-27 08:00" },
    { id: 2, name: "Weekly Client Summary", reportType: "client", cron: "0 9 * * 1", emails: ["manager@navig8.com"], active: true, lastSent: "2025-11-24 09:00" },
  ]);

  const handleDownloadReport = async (reportType: string) => {
    setIsGenerating(reportType);
    
    try {
      const response = await fetch(`/api/reports/generate/${reportType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ includeAI }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Report Generated",
        description: `Your ${reportType} report has been downloaded successfully.`,
      });
    } catch (error) {
      console.error('Report generation error:', error);
      toast({
        title: "Generation Failed",
        description: "There was an error generating the report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(null);
    }
  };

  const handleAddEmail = () => {
    if (newEmail && !emailRecipients.includes(newEmail) && newEmail.includes('@')) {
      setEmailRecipients([...emailRecipients, newEmail]);
      setNewEmail("");
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmailRecipients(emailRecipients.filter(e => e !== email));
  };

  const handleCreateSchedule = () => {
    if (!scheduleName || !selectedReportType || emailRecipients.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields and add at least one email recipient.",
        variant: "destructive",
      });
      return;
    }

    const newSchedule = {
      id: schedules.length + 1,
      name: scheduleName,
      reportType: selectedReportType,
      cron: cronSchedule,
      emails: emailRecipients,
      active: isActive,
      lastSent: null,
    };

    setSchedules([...schedules, newSchedule]);
    setScheduleDialogOpen(false);
    setScheduleName("");
    setSelectedReportType("");
    setCronSchedule("0 8 * * *");
    setEmailRecipients([]);
    setNewEmail("");
    setIsActive(true);

    toast({
      title: "Schedule Created",
      description: "Your report schedule has been created successfully.",
    });
  };

  const handleDeleteSchedule = (id: number) => {
    setSchedules(schedules.filter(s => s.id !== id));
    toast({
      title: "Schedule Deleted",
      description: "The report schedule has been removed.",
    });
  };

  const getReportTypeName = (typeId: string) => {
    return REPORT_TYPES.find(t => t.id === typeId)?.name || typeId;
  };

  const getCronDescription = (cron: string) => {
    if (cron === "0 8 * * *") return "Daily at 8:00 AM";
    if (cron === "0 9 * * 1") return "Every Monday at 9:00 AM";
    if (cron === "0 9 1 * *") return "First day of month at 9:00 AM";
    if (cron === "0 6 * * *") return "Daily at 6:00 AM";
    if (cron === "0 18 * * *") return "Daily at 6:00 PM";
    return cron;
  };

  const getReportIcon = (typeId: string) => {
    const report = REPORT_TYPES.find(t => t.id === typeId);
    return report ? report.icon : FileText;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)]">Reports Hub</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate professional PDF reports with AI-powered insights
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 p-2 bg-[hsl(215,28%,12%)] rounded-lg border border-[hsl(215,28%,18%)]">
            <Sparkles className="w-4 h-4 text-[hsl(24.6,95%,53%)]" />
            <span className="text-xs text-muted-foreground">AI Insights:</span>
            <button
              onClick={() => setIncludeAI(!includeAI)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                includeAI ? "bg-[hsl(24.6,95%,53%)]" : "bg-gray-600"
              }`}
              data-testid="toggle-ai-insights"
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  includeAI ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
          </div>
          <Button 
            onClick={() => setScheduleDialogOpen(true)}
            className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium"
            data-testid="button-new-schedule"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Report
          </Button>
        </div>
      </div>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {REPORT_TYPES.map((report) => {
          const IconComponent = report.icon;
          const isLoading = isGenerating === report.id;

          return (
            <Card 
              key={report.id}
              className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)] hover:border-[hsl(215,28%,25%)] transition-colors"
              data-testid={`card-report-${report.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: `${report.color}20` }}
                  >
                    <IconComponent className="w-6 h-6" style={{ color: report.color }} />
                  </div>
                  <Badge 
                    variant="outline" 
                    className="text-xs"
                    style={{ borderColor: report.color, color: report.color }}
                  >
                    {report.frequency}
                  </Badge>
                </div>
                <CardTitle className="text-lg text-white mt-3">{report.name}</CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  {report.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Features List */}
                <div className="space-y-2">
                  {report.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => handleDownloadReport(report.id)}
                    disabled={isLoading}
                    className="flex-1 bg-[hsl(215,28%,14%)] hover:bg-[hsl(215,28%,18%)] text-white border border-[hsl(215,28%,25%)]"
                    data-testid={`button-download-${report.id}`}
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      setSelectedReportType(report.id);
                      setScheduleDialogOpen(true);
                    }}
                    className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
                    data-testid={`button-schedule-${report.id}`}
                  >
                    <Calendar className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* AI-Powered Features Banner */}
      <Card className="bg-gradient-to-r from-[hsl(215,28%,10%)] to-[hsl(215,28%,14%)] border-[hsl(24.6,95%,53%)]/30">
        <CardContent className="py-6">
          <div className="flex items-center gap-6">
            <div className="p-4 rounded-full bg-[hsl(24.6,95%,53%)]/20">
              <Sparkles className="w-8 h-8 text-[hsl(24.6,95%,53%)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">AI-Powered Executive Summaries</h3>
              <p className="text-sm text-muted-foreground">
                Each report includes an AI-generated executive summary that analyzes your data and provides 
                actionable insights, trend analysis, and recommendations tailored to your operations.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span>Trend Analysis</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <BarChart3 className="w-4 h-4 text-[hsl(187,100%,42%)]" />
                <span>Performance Metrics</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Activity className="w-4 h-4 text-[hsl(24.6,95%,53%)]" />
                <span>Actionable Insights</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduled Reports Section */}
      <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
        <CardHeader>
          <CardTitle className="text-[hsl(187,100%,42%)] flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Scheduled Reports
            </span>
            <Badge variant="secondary" className="bg-[hsl(187,100%,42%)]/20 text-[hsl(187,100%,42%)]">
              {schedules.length} Active
            </Badge>
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Automated PDF reports delivered to your inbox on schedule
          </CardDescription>
        </CardHeader>
        <CardContent>
          {schedules.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No report schedules configured yet</p>
              <Button 
                onClick={() => setScheduleDialogOpen(true)}
                className="mt-4 bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black"
              >
                Create Your First Schedule
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {schedules.map(schedule => {
                const ReportIcon = getReportIcon(schedule.reportType);
                const report = REPORT_TYPES.find(r => r.id === schedule.reportType);

                return (
                  <div 
                    key={schedule.id} 
                    className="p-4 bg-[hsl(215,28%,12%)] rounded-lg border border-[hsl(215,28%,18%)] flex items-center gap-4"
                    data-testid={`schedule-${schedule.id}`}
                  >
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${report?.color || 'hsl(187,100%,42%)'}20` }}
                    >
                      <ReportIcon className="w-5 h-5" style={{ color: report?.color || 'hsl(187,100%,42%)' }} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-white">{schedule.name}</h4>
                        <Badge className={schedule.active ? "bg-green-500/20 text-green-500" : "bg-gray-500/20 text-gray-500"}>
                          {schedule.active ? "Active" : "Paused"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{getReportTypeName(schedule.reportType)}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {getCronDescription(schedule.cron)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {schedule.emails.length} recipient{schedule.emails.length !== 1 ? 's' : ''}
                        </span>
                        {schedule.lastSent && (
                          <span className="text-muted-foreground/70">
                            Last sent: {schedule.lastSent}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => handleDownloadReport(schedule.reportType)}
                        className="text-[hsl(187,100%,42%)] hover:bg-[hsl(187,100%,42%)]/20"
                        data-testid={`button-download-schedule-${schedule.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        className="text-red-500 hover:bg-red-500/20"
                        data-testid={`button-delete-schedule-${schedule.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-lg bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">Create Report Schedule</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Set up automated PDF report delivery with AI-powered insights
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Schedule Name */}
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Schedule Name</label>
              <Input
                placeholder="e.g., Daily Operations Report"
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
                className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                data-testid="input-schedule-name"
              />
            </div>

            {/* Report Type */}
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Report Type</label>
              <Select value={selectedReportType} onValueChange={setSelectedReportType}>
                <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" style={{ color: type.color }} />
                        <span>{type.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Schedule Frequency */}
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Schedule Frequency</label>
              <Select value={cronSchedule} onValueChange={setCronSchedule}>
                <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0 6 * * *">Daily at 6:00 AM</SelectItem>
                  <SelectItem value="0 8 * * *">Daily at 8:00 AM</SelectItem>
                  <SelectItem value="0 18 * * *">Daily at 6:00 PM</SelectItem>
                  <SelectItem value="0 9 * * 1">Weekly (Monday 9:00 AM)</SelectItem>
                  <SelectItem value="0 9 1 * *">Monthly (1st at 9:00 AM)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Reports will be generated and emailed at this schedule
              </p>
            </div>

            {/* Email Recipients */}
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Email Recipients</label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddEmail()}
                  className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                  data-testid="input-email"
                />
                <Button 
                  onClick={handleAddEmail}
                  className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black"
                  size="icon"
                  data-testid="button-add-email"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {emailRecipients.length > 0 && (
                <div className="space-y-2">
                  {emailRecipients.map(email => (
                    <div key={email} className="flex items-center justify-between p-2 bg-[hsl(215,28%,12%)] rounded border border-[hsl(215,28%,18%)]">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3 text-muted-foreground" />
                        <span className="text-sm text-white">{email}</span>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleRemoveEmail(email)}
                        className="w-6 h-6 text-red-500 hover:bg-red-500/20"
                        data-testid={`button-remove-email-${email}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-[hsl(215,28%,12%)] rounded border border-[hsl(215,28%,18%)]">
              <div>
                <label className="text-sm text-[hsl(187,100%,42%)]">Active Schedule</label>
                <p className="text-xs text-muted-foreground">Enable automatic report delivery</p>
              </div>
              <button
                onClick={() => setIsActive(!isActive)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isActive ? "bg-[hsl(24.6,95%,53%)]" : "bg-gray-600"
                }`}
                data-testid="toggle-active-schedule"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isActive ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setScheduleDialogOpen(false)}
              className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
              data-testid="button-cancel-schedule"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSchedule}
              className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium"
              data-testid="button-create-schedule"
            >
              Create Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
